import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';

/**
 * Handle user logins
 */
export class AuthService {
	/**
	 * @param {Api} Api	 Instance of Api Service for making XHR requests
	 * @param {Storage} storage  Instance of Storage for storing token
	 * @returns {undefined}
	 */
	constructor(Api, storage, SecureStorage, Logger) {
		'ngInject';
		/**
		 * The Api Service
		 * @type {Api}
		 */
		this.Api = Api;
		/**
		 * Instance of Storage for storing JWT auth token
		 * {Storage} storage
		 */
		this.storage = storage;
		/**
		 * Instance of SecureStorageService for storing JWT in keychain on iOS
		 * {SecureStorage} secureStorage
		 */
		this.secureStorage = SecureStorage;
		/**
		 * Instance of LoggerService
		 * {LoggerService} Logger
		 */
		this.Logger = Logger;
	}

	/**
	 * Figure out what type of auth we should use for the supplied email address
	 * @param {String} email Email address of user
	 * @returns {Promise}
	 */
	getTypeByEmail(email) {
		return this.Api.get('/auth/type', { email }, { Authorization: 'Public' });
	}

	/**
	 * Send password reset link to the specified email address
	 * @param {String} email Email address of user
	 * @returns {Promise}
	 */
	sendResetLink(email) {
		return this.Api.post('/users/forgot_password', { email }, { Authorization: 'Public' });
	}

	/**
	 * Attempt auth using the supplied email and password
	 * @param {String} email Email address of user
	 * @param {String} password User's password
	 * @param {Boolean} remember_me Whether user wants remember me cookie set
	 * @returns {Promise}
	 */
	login(email, password, remember_me) {
		return this.Api.post('/auth/login', { email, password, remember_me }, { Authorization: 'Public' }).then(data => {
			if (data.success) {
				this.setToken(data);
			}
			return data;
		});
	}

	/**
	 * Store a JWT token in browser storage and secure storage (keychain on iOS)
	 * @param {Object} tokenData Object containing the JWT token
	 */
	setToken(tokenData) {
		this.storage.setItem('jwt', tokenData);
		this.secureStorage.set('token', tokenData.token);
		this.secureStorage.set('sub', tokenData.payload.sub);
		this.secureStorage.set('base_url', window.BASE_URL);
		this.Logger.write('APP_LOGIN_DEBUG', 'Setting JWT without exception', {
			tokenData,
			base_url: window.BASE_URL,
			btFile: 'AuthService.js',
			btLine: 80,
		});
	}

	/**
	 * Check for a valid login token (JWT)
	 * @returns {boolean} Whether user is authenticated
	 */
	isLoggedIn() {
		let jwt = this.storage.getItem('jwt');
		let now = +new Date() / 1000;
		return !!(jwt && jwt.token && jwt.payload && jwt.payload.exp > now);
	}

	/**
	 * Check whether the JWT token is >= 67% into its lifetime
	 * @returns {boolean|undefined} Percentage of lifetime that has passed or undefined if already expired
	 */
	isExpiringSoon() {
		if (!this.isLoggedIn()) {
			return undefined;
		}
		let jwt = this.storage.getItem('jwt');
		let claims = jwt.payload;
		let percentUsed = (+new Date() - claims.iat) / (claims.exp - claims.iat);
		return percentUsed > 0.67;
	}

	/**
	 * Gets a new JWT (JSON Web Token) with the same claims but a new expiration date
	 * @returns {Promise} Promise that is resolved to false or new JWT token
	 */
	async refresh(hubId) {
		if (!this.isLoggedIn()) {
			return Promise.resolve(false);
		}
		const data = await this.Api.post('/auth/bearer/refresh', { hub_id: hubId }, { avoidLoadingBar: true });
		if (data && data.payload) {
			this.setToken(data);
			return Promise.resolve(true);
		}
		return Promise.resolve(false);
	}

	/**
	 * Log the user out
	 */
	logout() {
		this.storage.removeItem('jwt');
		// this works. not sure if there's a better way?
		document.cookie.split(';').forEach(function (c) {
			document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/');
		});
		if (this.storage.getItem('openid') && window.plugins && window.plugins.googleplus) {
			window.plugins.googleplus.logout(
				msg => {},
				msg => {}
			);
		}
		ApiResourcify.clearCache();
	}

	/**
	 * Send a Google ID Token and receive a new JWT in return
	 * @param {String} idToken  String containing the Google ID Token to verify
	 * @param {String} email  The email of the user to authorize
	 * @param {String} name  The users first and last name
	 * @returns {Promise}
	 */
	verifyGoogleIdToken(idToken, email, name) {
		return this.Api.post(
			'/auth/google_verify',
			{
				id_token: idToken,
				email: email,
				name: name,
			},
			{ Authorization: 'Public' }
		).then(data => {
			if (data) {
				this.storage.setItem('openid', true);
				this.setToken(data);
			} else {
				this.logout();
			}
			return data;
		});
	}
}
